import type { ScanResults } from './health.types';

export interface CareplyxError {
  code: string;
  message: string;
}

export interface CarePlixTokenResponse {
  statusCode: number;
  scan_token: string;
  token_expiry: string;
  message?: string;
}

export interface CarePlixVitals {
  heart_rate: number | string;
  resp_rate: number | string;
  oxy_sat_prcnt: number | string;
  bp_sys: number | string;
  bp_dia: number | string;
}

export interface CarePlixAddScanResponse {
  statusCode: number;
  scan_id: string;
  wellness_score: number;
  health_risk_score?: number | string;
  posture?: string;
  vitals: CarePlixVitals;
  metadata: {
    heart_scores: {
      sdnn: number | string;
      rmssd: number | string;
      pNN50_per: number | string;
      stress_index: number | string;
    };
    cardiovascular: {
      cardiac_out: number | string;
    };
    physiological_scores: {
      bmi: number | string;
      bloodvolume: number | string;
      tbw: number | string;
      waterpercent?: number | string;
      bodyfat: number | string;
      weight: number | string;
    };
    glucose_info?: {
      status?: string;
      diabetes_risk_score: number | string;
      is_diabetic: boolean;
    };
  };
  message?: string;
}

export interface ScanTokenState {
  token: string;
  expiresAt: number;
}

export interface ScanFinishPayload {
  raw_intensity: unknown;
  ppg_time: unknown;
  average_fps: number;
}

export interface FaceScanElements {
  videoElement: HTMLVideoElement;
  canvasElement: HTMLCanvasElement;
}

export type FrameCallback = (frame: {
  type: 'error' | 'calibration' | 'scan';
  message: string;
  progress: number;
  timeElapsed: number;
  isLiteMode: boolean;
}) => void;

export function mapCarePlixResponse(raw: CarePlixAddScanResponse): ScanResults {
  const num = (v: number | string | undefined, fallback = 0): number => {
    if (v === undefined || v === '--') return fallback;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const stressIndex = num(raw.metadata.heart_scores.stress_index);
  let stressStatus: ScanResults['stress']['status'] = 'normal';
  if (stressIndex >= 150) stressStatus = 'high';
  else if (stressIndex >= 100) stressStatus = 'elevated';

  return {
    healthScore: num(raw.wellness_score),
    scannedAt: new Date().toISOString(),
    sessionId: raw.scan_id,
    vitals: {
      heartRate: num(raw.vitals.heart_rate),
      respirationRate: num(raw.vitals.resp_rate),
      oxygenSaturation: num(raw.vitals.oxy_sat_prcnt),
      systolicBP: num(raw.vitals.bp_sys),
      diastolicBP: num(raw.vitals.bp_dia),
      glucose: raw.metadata.glucose_info
        ? {
            diabetesRiskScore: num(raw.metadata.glucose_info.diabetes_risk_score),
            isDiabetic: raw.metadata.glucose_info.is_diabetic,
          }
        : undefined,
    },
    heart: {
      sdnn: num(raw.metadata.heart_scores.sdnn),
      rmssd: num(raw.metadata.heart_scores.rmssd),
      pnn50: num(raw.metadata.heart_scores.pNN50_per),
      cardiacOutput: num(raw.metadata.cardiovascular.cardiac_out),
    },
    physiology: {
      bmi: num(raw.metadata.physiological_scores.bmi),
      bloodVolumeMl: num(raw.metadata.physiological_scores.bloodvolume),
      totalBodyWaterLitres: num(raw.metadata.physiological_scores.tbw),
      bodyWaterPercent: num(raw.metadata.physiological_scores.waterpercent),
      totalBodyFatPercent: num(raw.metadata.physiological_scores.bodyfat),
      bodyWeightKg: num(raw.metadata.physiological_scores.weight),
    },
    stress: {
      stressIndex,
      status: stressStatus,
    },
  };
}
